import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { CreateWorkOrderDialog } from '@/components/work-orders/CreateWorkOrderDialog';
import { SnapshotKPIs } from '@/components/dashboard/SnapshotKPIs';
import { NextBestAction } from '@/components/dashboard/NextBestAction';
import { ExecutionFocusTabs } from '@/components/dashboard/ExecutionBoard';
import { MoldAlertsSidebar } from '@/components/dashboard/MoldAlertsSidebar';
import { LineDownBanner } from '@/components/dashboard/LineDownBanner';
import { MyActiveWork } from '@/components/dashboard/MyActiveWork';
import { HandoffNotesCard } from '@/components/dashboard/HandoffNotesCard';
import { CapacityPanel } from '@/components/dashboard/CapacityPanel';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { fetchProfiles } from '@/lib/api/profiles';
import { usePMAutoGeneration } from '@/hooks/usePMAutoGeneration';
import { useAutoEscalation } from '@/hooks/useAutoEscalation';
import { useOrgSettings } from '@/hooks/useOrgSettings';

export default function Dashboard() {
  const { t } = useTranslation(['common', 'maintenance']);
  const [createWOOpen, setCreateWOOpen] = useState(false);
  const { workOrders, loading: woLoading } = useWorkOrders();
  const { user } = useAuth();

  const { settings } = useOrgSettings();
  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  // Auto-generate PM work orders when plans become overdue
  usePMAutoGeneration(workOrders, !woLoading);

  // Auto-escalate aged WOs for managers
  useAutoEscalation(isManager && !woLoading ? workOrders : [], settings.wo_aging_threshold_days ?? 3);

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles', user?.organization_id],
    queryFn: () => fetchProfiles(user?.organization_id ?? null),
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });

  // Technicians = everyone who isn't super_admin
  const technicians = profiles
    .filter(p => p.role !== 'super_admin')
    .map(p => ({ id: p.id, name: p.name }));

  if (woLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <img src="/logo.png" alt="MoldOp" className="w-36 object-contain opacity-60 mix-blend-multiply dark:mix-blend-normal" />
        <div className="w-36 h-[2px] rounded-full bg-border overflow-hidden">
          <div className="h-full w-1/3 rounded-full hot-runner" style={{ animation: 'slide-bar 1.4s cubic-bezier(0.4,0,0.2,1) infinite' }} />
        </div>
        <p className="text-sm text-muted-foreground">{t('common:state.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t('common:nav.dashboard')}
        actions={
          <Button size="sm" onClick={() => setCreateWOOpen(true)} className="h-9">
            <Plus className="h-4 w-4 mr-1.5" />
            {t('maintenance:wo.create_title')}
          </Button>
        }
      />

      <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
        {/* Line Down Alert — always full-width, renders null when no line-down events */}
        <LineDownBanner workOrders={workOrders} />

        {/* Two-column command center layout */}
        <div className="grid gap-5 xl:grid-cols-[1fr_320px]">

          {/* ── Left: action-oriented main column ── */}
          <div className="space-y-4 min-w-0">
            {/* My Active Work with inline Finish/Block (technicians only) */}
            <MyActiveWork workOrders={workOrders} />

            {/* Next Best Action / Top Priorities */}
            <NextBestAction workOrders={workOrders} />

            {/* Tabbed execution focus: Critical / Ready / Blocked */}
            <ExecutionFocusTabs workOrders={workOrders} />
          </div>

          {/* ── Right: status + capacity sidebar ── */}
          <div className="space-y-4">
            {/* Compact KPI snapshot */}
            <SnapshotKPIs workOrders={workOrders} compact />

            {/* Team capacity */}
            <CapacityPanel workOrders={workOrders} technicians={technicians} />

            {/* Shift handoff notes — renders null when no active handoffs */}
            <HandoffNotesCard workOrders={workOrders} />

            {/* Mold health alerts */}
            <MoldAlertsSidebar />
          </div>
        </div>
      </div>

      <CreateWorkOrderDialog open={createWOOpen} onOpenChange={setCreateWOOpen} />
    </div>
  );
}
